import { getReferenceString, MedplumClient, WithId } from "@medplum/core";
import type { Flag, Group } from "@medplum/fhirtypes";

import { LIBRARY_FLAG_IDENTIFIER_SYSTEM } from "@/fhir/constants/identifier";
import { isMedplumNotFoundError } from "@/utils/medplum-forbidden";
import { collectResourcesFromNextPages } from "@/utils/pagination";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { ACTIVE_FLAG_STATUS } from "../constants/flags";
import { buildActiveFlagQuery } from "../utils/flag-query";

import { fetchFlagLibraryGroup } from "./flag-library-group";

/**
 * Reaching a practice's library entries: resolving the Group they hang off, searching them, reading
 * one, and building the query that keeps a code unique. Every operation in `./flags` starts here.
 *
 * Each helper takes the failure reasons it should answer with, rather than owning an enum of its
 * own. The four operations name the same underlying facts differently — a read calls a broken
 * lookup `LookupFailed`, a save calls it the same but alongside reasons a read has no use for — so
 * the reason belongs to the operation and the mechanism belongs here.
 */

/**
 * A practice's library is a curated set — tens of entries, not thousands — so one page usually holds
 * all of it. A larger library still reads whole: `searchLibraryFlags` follows `next` links.
 */
const LIBRARY_FLAG_SEARCH_COUNT = 200;

/**
 * Every active library entry for one practice. `subject` is what scopes the search: it is the
 * practice's Flag Library Group, so this can never reach another practice's library or a patient's
 * flags.
 *
 * Uncached deliberately. `MedplumClient` holds search responses for 60 seconds by default, which is
 * long enough to outlive a write: the re-read that should show a new flag would be answered from
 * the bundle fetched before it was written. Both callers need the current library — the page after
 * a save or a refresh, and the duplicate check that decides whether a code is free.
 *
 * Every page is read, and a failed page throws. A partial library would read as entries that are
 * not there: the client list's flag filter drops applied ids missing from it.
 */
export async function searchLibraryFlags(
  medplum: MedplumClient,
  groupReference: string,
): Promise<WithId<Flag>[]> {
  const firstPage = await medplum.searchResources(
    "Flag",
    {
      subject: groupReference,
      status: ACTIVE_FLAG_STATUS,
      _count: String(LIBRARY_FLAG_SEARCH_COUNT),
    },
    { cache: "no-cache" },
  );

  const nextPages = await collectResourcesFromNextPages<WithId<Flag>>(
    medplum,
    firstPage.bundle,
    "Flag",
    { throwOnPageError: true },
  );

  return [...firstPage, ...nextPages];
}

/**
 * Library entries by id, deleted ones included, for a chart read to join against.
 *
 * Deleting only deactivates an entry, so the library keeps growing and would eventually overflow one
 * page of a whole-library search. Searching by id keeps the result as small as what the charts use.
 *
 * `subject` limits the search to this practice's Group, so an id from another practice's library
 * returns nothing. Uncached for the same reason as `searchLibraryFlags`.
 *
 * @param flagIds - Never empty
 */
export async function searchLibraryFlagsByIds(
  medplum: MedplumClient,
  groupReference: string,
  flagIds: string[],
): Promise<WithId<Flag>[]> {
  return await medplum.searchResources(
    "Flag",
    {
      _id: flagIds.join(","),
      subject: groupReference,
      _count: String(flagIds.length),
    },
    { cache: "no-cache" },
  );
}

/**
 * Resolves a practice's Flag Library Group to the reference its entries hang off.
 *
 * Every operation starts here, and each has to tell the same two failures apart — a lookup that
 * broke (retryable, and reported) from a practice that has no Group at all (a data problem
 * onboarding or the backfill missed, which no retry fixes).
 *
 * The Group is never created on this path: healing the gap on a user's first save or first page
 * load would hide the fact that a practice was missed. See `createPracticeFlag`.
 */
export async function resolveLibraryGroupReference<TReason>(
  medplum: MedplumClient,
  practiceId: string,
  reasons: { lookupFailed: TReason; missingLibraryGroup: TReason },
): Promise<{ ok: true; reference: string } | { ok: false; reason: TReason }> {
  let libraryGroup: Group | undefined;
  try {
    // Uncached for the same reason as the library search, and one case of its own: a practice with
    // no Group caches that miss, so a refresh after the backfill runs would keep reporting
    // `MissingLibraryGroup` until the entry aged out.
    libraryGroup = await fetchFlagLibraryGroup(medplum, practiceId, { cache: "no-cache" });
  } catch (error) {
    captureExceptionWithoutPhi("Failed to read the practice flag library group", error, {
      practiceId,
    });
    return { ok: false, reason: reasons.lookupFailed };
  }

  const reference = libraryGroup ? getReferenceString(libraryGroup) : undefined;

  // An unreferenceable Group is as unusable as a missing one — never fall back to another subject.
  if (!reference) {
    return { ok: false, reason: reasons.missingLibraryGroup };
  }

  return { ok: true, reference };
}

/**
 * Resolves a practice's Flag Library Group and reads its active entries in one step.
 *
 * The two always travel together for callers that need the library itself — the library page
 * renders it, the apply checks staged ids against it — and both steps answer with the same
 * `lookupFailed`, so they share one call rather than each caller repeating the preamble.
 *
 * Callers that only need the reference take `resolveLibraryGroupReference`: an edit or a delete
 * reads its one entry by id, and a read that pairs the library search with a search of its own runs
 * the two in parallel instead.
 */
export async function loadPracticeLibrary<TReason>(
  medplum: MedplumClient,
  practiceId: string,
  reasons: { lookupFailed: TReason; missingLibraryGroup: TReason },
): Promise<
  { ok: true; groupReference: string; flags: WithId<Flag>[] } | { ok: false; reason: TReason }
> {
  const libraryGroup = await resolveLibraryGroupReference(medplum, practiceId, reasons);

  if (!libraryGroup.ok) {
    return { ok: false, reason: libraryGroup.reason };
  }

  let flags: WithId<Flag>[];
  try {
    flags = await searchLibraryFlags(medplum, libraryGroup.reference);
  } catch (error) {
    captureExceptionWithoutPhi("Failed to read the practice flag library", error, { practiceId });
    return { ok: false, reason: reasons.lookupFailed };
  }

  return { ok: true, groupReference: libraryGroup.reference, flags };
}

/**
 * Reads one library entry by id, and confirms it belongs to this practice's library.
 *
 * The subject check is what makes the id safe to act on. Ids arrive from the page's own rows today,
 * so a mismatch means something is mis-wired rather than malicious — but an edit or a delete writes
 * through this, and a foreign practice's Flag reaching either would be a cross-tenant write. It
 * reads as `notFound` because from this practice's side there is no such entry.
 *
 * Uncached, so an edit works from the entry as it stands rather than from one read up to a minute
 * ago and possibly already changed by a colleague.
 */
export async function readLibraryFlag<TReason>(
  medplum: MedplumClient,
  flagId: string,
  groupReference: string,
  reasons: { lookupFailed: TReason; notFound: TReason },
): Promise<{ ok: true; flag: WithId<Flag> } | { ok: false; reason: TReason }> {
  let flag: WithId<Flag>;
  try {
    flag = await medplum.readResource("Flag", flagId, { cache: "no-cache" });
  } catch (error) {
    if (isMedplumNotFoundError(error)) {
      return { ok: false, reason: reasons.notFound };
    }

    captureExceptionWithoutPhi("Failed to read a practice library flag", error, { flagId });
    return { ok: false, reason: reasons.lookupFailed };
  }

  if (flag.subject?.reference !== groupReference) {
    return { ok: false, reason: reasons.notFound };
  }

  return { ok: true, flag };
}

/**
 * The query that pins a derived code to one active entry of one practice's library — the
 * `If-None-Exist` guard a create is sent with, and the search a rename checks itself against. See
 * `buildActiveFlagQuery` for the shape.
 */
export function buildLibraryFlagCodeQuery(groupReference: string, code: string): string {
  return buildActiveFlagQuery({
    system: LIBRARY_FLAG_IDENTIFIER_SYSTEM,
    value: code,
    subject: groupReference,
  });
}
